import { useState } from "react";
import { useTranslation } from "react-i18next";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { LinkButton, type LinkButtonProps } from "~/common/components/Link";
import { requestConfirmation } from "~/AddToCart/ModalSevice";
import ConfirmationModal from "~/common/components/ConfirmationModal";
import { CircularProgress, Portal, Typography } from "@mui/material";
import { addToComparison, clearComparisonList } from "~/ProductComparison/redux/actions";
import { useDispatch } from "react-redux";
import { ProductComparisonSnackbar } from "~/ProductComparison/ProductComparisonCheckbox";

type Props = LinkButtonProps & {
  products: string[];
};

export const MMDCompareProductsButton = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [baseMessage, setBaseMessage] = useState("");
  const [error, setError] = useState(false);

  const handleClick = async () => {
    const hasCompareProducts = window.SCHA.productComparisonList.currentSize > 0;
    if (hasCompareProducts) {
      const confirmed = await requestConfirmation(({ onConfirm, onCancel }) => {
        return (
          <ConfirmationModal
            okLabel={t("feature.mmd.assistant.compareProducts.modal.yes")}
            cancelLabel={t("cancel")}
            onOk={onConfirm}
            onCancel={onCancel}
          >
            <Typography variant="bodyLarge">{t("feature.mmd.assistant.compareProducts.modal.confirm")}</Typography>
          </ConfirmationModal>
        );
      });
      if (!confirmed) {
        return;
      }
    }
    setLoading(true);
    if (hasCompareProducts) {
      await dispatch(clearComparisonList());
    }
    try {
      for (const product of props.products) {
        await dispatch(addToComparison(product));
      }
      setBaseMessage("comparisonSnack.added");
      setError(false);
    } catch (error) {
      console.error(error);
      setBaseMessage("");
      setError(true);
    } finally {
      setLoading(false);
      setShowSnackbar(true);
    }
  };

  return (
    <>
      <LinkButton
        {...props}
        iconBefore={loading ? <CircularProgress size={24} /> : <OpenInNewIcon />}
        disabled={loading}
        onClick={handleClick}
        data-test-id="mmd-compare-products-button"
      >
        {t("feature.mmd.assistant.compareProducts.button")}
      </LinkButton>
      <Portal container={document.body}>
        <ProductComparisonSnackbar
          open={showSnackbar}
          message={baseMessage}
          apiError={error}
          onClose={() => setShowSnackbar(false)}
          newTab
        />
      </Portal>
    </>
  );
};
